// =============================================================================
// 実3Dオブジェクト生成。ミラーのバンドルはTHREEをexportしないので、捕捉済みシーンの
// インスタンスからコンストラクタ(Mesh/Group/BufferGeometry/BufferAttribute/標準材質)を
// 「収穫」して、プリミティブ形状を組み立てる。海面シェーダには触れない（不透明な陸上の
// 小物を追加するだけ＝水面の反射/屈折/深度に悪影響を与えない）。
// =============================================================================
let T: any = null;

export function harvestTHREE(scene: any): any {
  if (T) return T;
  let mesh: any = null, group: any = null, stdCtor: any = null;
  scene.traverse((o: any) => {
    if (o.isMesh && !mesh) mesh = o;
    if (o.isGroup && !group) group = o;
    if (!stdCtor && o.isMesh && o.material) {
      const m = Array.isArray(o.material) ? o.material[0] : o.material;
      if (m && (m.isMeshStandardMaterial || m.isMeshPhysicalMaterial)) stdCtor = m.constructor;
    }
  });
  if (!mesh) return null;
  T = {
    Vector3: mesh.position.constructor,
    Mesh: mesh.constructor,
    Group: group ? group.constructor : mesh.constructor, // Group が無ければ Mesh で代用しない用途のみ
    BufferGeometry: mesh.geometry.constructor,
    BufferAttribute: mesh.geometry.attributes.position.constructor,
    StdMat: stdCtor,
  };
  return T;
}

// ---- 形状ビルダー（手書き頂点 → BufferGeometry）----
// uv を渡すとテクスチャ用のUVも張る(toNonIndexedが展開するので属性countは必ず一致する)。
function makeGeom(pos: number[], idx: number[], uv?: number[]): any {
  const g = new T.BufferGeometry();
  g.setAttribute('position', new T.BufferAttribute(new Float32Array(pos), 3));
  if (uv && uv.length === (pos.length / 3) * 2) g.setAttribute('uv', new T.BufferAttribute(new Float32Array(uv), 2));
  g.setIndex(idx);
  // WebKit(Safari)のWebGPUは属性countの不一致(normal/uvがpositionと違う数)を弾いて描画しない。
  // indexedのまま computeVertexNormals すると normal が壊れる(count不一致)ため、
  // toNonIndexed で index展開して全属性のcountを揃えてから法線を計算する。
  const g2 = g.toNonIndexed ? g.toNonIndexed() : g;
  g2.computeVertexNormals();
  return g2;
}

function cylinderGeom(rTop: number, rBot: number, h: number, seg: number, yBase = 0): any {
  const pos: number[] = [], idx: number[] = [];
  for (let i = 0; i <= seg; i++) {
    const a = (i / seg) * Math.PI * 2, c = Math.cos(a), s = Math.sin(a);
    pos.push(c * rBot, yBase, s * rBot);
    pos.push(c * rTop, yBase + h, s * rTop);
  }
  for (let i = 0; i < seg; i++) {
    const b0 = i * 2, t0 = i * 2 + 1, b1 = (i + 1) * 2, t1 = (i + 1) * 2 + 1;
    idx.push(b0, b1, t1, b0, t1, t0);
  }
  const n = pos.length / 3;
  pos.push(0, yBase, 0); pos.push(0, yBase + h, 0);
  const bc = n, tc = n + 1;
  for (let i = 0; i < seg; i++) {
    const b0 = i * 2, b1 = (i + 1) * 2, t0 = i * 2 + 1, t1 = (i + 1) * 2 + 1;
    if (rBot > 0.001) idx.push(bc, b1, b0);
    if (rTop > 0.001) idx.push(tc, t0, t1);
  }
  return makeGeom(pos, idx);
}
const coneGeom = (r: number, h: number, seg: number, yBase = 0) => cylinderGeom(0.001, r, h, seg, yBase);

// UV付き円筒(u=周方向, v=長手×タイル)。テクスチャ付き丸太用。uOff/vOffで個体差。
// aoAxis: 断面のどの成分がworld下向きになるか('sin'=along z, 'cos'=along x)。下側を暗くして立体感を出す。
function cylinderGeomUV(r: number, h: number, seg: number, vTile: number, uOff: number, vOff: number, taper = 1, aoAxis: 'sin' | 'cos' | 'none' = 'none', uScale = 1): any {
  const pos: number[] = [], idx: number[] = [], uv: number[] = [];
  for (let i = 0; i <= seg; i++) {
    // uScale<1 = テクスチャ全幅の一部だけを周に巻く(細いポールで横ディテールが縦縞に潰れるのを防ぐ)
    const a = (i / seg) * Math.PI * 2, c = Math.cos(a), s = Math.sin(a), u = (i / seg) * uScale + uOff;
    pos.push(c * r, 0, s * r); uv.push(u, vOff);
    pos.push(c * r * taper, h, s * r * taper); uv.push(u, vOff + h / vTile);
  }
  for (let i = 0; i < seg; i++) {
    const b0 = i * 2, t0 = i * 2 + 1, b1 = (i + 1) * 2, t1 = (i + 1) * 2 + 1;
    idx.push(b0, b1, t1, b0, t1, t0);
  }
  const n = pos.length / 3;
  pos.push(0, 0, 0); uv.push(0.5, vOff);
  pos.push(0, h, 0); uv.push(0.5, vOff + h / vTile);
  for (let i = 0; i < seg; i++) {
    const b0 = i * 2, b1 = (i + 1) * 2, t0 = i * 2 + 1, t1 = (i + 1) * 2 + 1;
    idx.push(n, b1, b0); idx.push(n + 1, t0, t1);
  }
  const geo = makeGeom(pos, idx, uv);
  if (aoAxis !== 'none') {
    const pa = geo.attributes.position, n2 = pa.count, col = new Float32Array(n2 * 3);
    for (let i2 = 0; i2 < n2; i2++) {
      const x = pa.getX(i2), z = pa.getZ(i2);
      const rr = Math.hypot(x, z) || 1;
      const down = aoAxis === 'sin' ? z / rr : x / rr; // 下向き成分(-1..1)
      const t = 1 - 0.45 * Math.max(0, down);          // 下面ほど暗く(疑似AO)
      col[i2 * 3] = t; col[i2 * 3 + 1] = t; col[i2 * 3 + 2] = t;
    }
    geo.setAttribute('color', new T.BufferAttribute(col, 3));
  }
  return geo;
}

// 標準材質を新規生成（テクスチャ無しの単色）
function mat(hex: number): any {
  if (T.StdMat) return new T.StdMat({ color: hex, roughness: 0.92, metalness: 0, side: 2 });
  return null;
}

// ---- いかだ(raft)プロトタイプ ----------------------------------------------
// 参考: Stranded Deep系/Sketchfab "raft"(古い木の丸太いかだ)。丸太を平行に並べた甲板＋
// 横木(クロスビーム)＋マスト＋帆を手続き生成。world単位(≒1m=20)で約2.6m×2.9mの筏。
// 海面シェーダには触れず、不透明な小物として scene へ追加するだけ。
// 頂点カラー対応の標準材質（vertexColorsで木目/布の濃淡を乗算 → のっぺり感を解消）
function matVC(hex: number, rough = 0.88): any {
  if (!T.StdMat) return null;
  return new T.StdMat({ color: hex, roughness: rough, metalness: 0, side: 2, vertexColors: true });
}
// 木目・濃淡の頂点カラーを付与（seedで個体差、形状座標で長手の縞＋微ノイズ）。白基準=材質colorに乗算。
function woodColors(geo: any, seed: number): any {
  const pos = geo.attributes.position, n = pos.count, col = new Float32Array(n * 3);
  let s = ((seed * 2654435761) >>> 0) || 1; const rnd = () => { s = (1103515245 * s + 12345) & 0x7fffffff; return s / 0x7fffffff; };
  const base = 0.55 + rnd() * 0.6; // 丸太ごとに大きく差をつける(0.55〜1.15)＝風化した木の不揃い感
  for (let i = 0; i < n; i++) {
    const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
    const ang = Math.atan2(z, x);
    const grainY = 0.68 + 0.32 * (0.5 + 0.5 * Math.sin(y * 0.95 + seed * 1.7)); // 長手の太い木目縞
    const streak = 0.84 + 0.16 * (0.5 + 0.5 * Math.sin(ang * 5 + y * 0.4 + seed)); // 周方向の木目筋
    const noise = 0.9 + 0.1 * Math.sin(x * 11 + z * 9 + seed);
    const t = Math.min(1.25, base * grainY * streak * noise);
    col[i * 3] = t; col[i * 3 + 1] = t * 0.95; col[i * 3 + 2] = t * 0.85; // 木の赤み
  }
  geo.setAttribute('color', new T.BufferAttribute(col, 3));
  return geo;
}
// 布の濃淡（上明るく下陰る＋縦の折り目）。生成り色。
function clothColors(geo: any): any {
  const pos = geo.attributes.position, n = pos.count, col = new Float32Array(n * 3);
  let ymin = Infinity, ymax = -Infinity; for (let i = 0; i < n; i++) { const y = pos.getY(i); if (y < ymin) ymin = y; if (y > ymax) ymax = y; }
  for (let i = 0; i < n; i++) {
    const x = pos.getX(i), y = pos.getY(i), tv = (y - ymin) / ((ymax - ymin) || 1);
    const seam = (Math.sin(tv * Math.PI * 8) > 0.86) ? 0.82 : 1;   // 横パネルの継ぎ目(縫い目の影)
    const dirt = tv < 0.22 ? 0.86 + 0.14 * (tv / 0.22) : 1;        // 裾の潮汚れ
    const grad = (0.58 + 0.42 * tv) * seam * dirt;                 // 上明るく下陰る
    const crease = 0.82 + 0.18 * (0.5 + 0.5 * Math.sin(x * 0.85)); // 縦の折り目
    const t = grad * crease * 0.86; // 全体をやや落とし夜間に帆が発光して見えるのを防ぐ
    col[i * 3] = t; col[i * 3 + 1] = t * 0.99; col[i * 3 + 2] = t * 0.93; // やや黄味(生成り)
  }
  geo.setAttribute('color', new T.BufferAttribute(col, 3));
  return geo;
}
// 同一位置の頂点の法線を平均してスムースシェーディングに(フラット面のカクカク解消)。
// 属性countは変えないのでWebKitの属性数一致制約にも安全。
function smoothNormals(geo: any): any {
  const pos = geo.attributes.position, nor = geo.attributes.normal, n = pos.count;
  const map = new Map<string, [number, number, number, number[]]>();
  for (let i = 0; i < n; i++) {
    const k = pos.getX(i).toFixed(3) + ',' + pos.getY(i).toFixed(3) + ',' + pos.getZ(i).toFixed(3);
    let a = map.get(k); if (!a) { a = [0, 0, 0, []]; map.set(k, a); }
    a[0] += nor.getX(i); a[1] += nor.getY(i); a[2] += nor.getZ(i); a[3].push(i);
  }
  for (const a of map.values()) {
    const l = Math.hypot(a[0], a[1], a[2]) || 1;
    for (const i of a[3]) nor.setXYZ(i, a[0] / l, a[1] / l, a[2] / l);
  }
  nor.needsUpdate = true;
  return geo;
}
// たわんだ帆（格子分割＋風で膨らむ曲面）。平面1枚より立体的＝のっぺりしない。
function sailGeom(w: number, h: number, y0: number): any {
  // 風を受けた帆: 上(ヤード固定)が広く下すぼまりの台形 + 中央の大きなふくらみ + 自由端(下)の波打ち
  const nx = 16, ny = 20, pos: number[] = [], idx: number[] = []; // 分割を細かくしてファセット(角張り)を消す
  for (let j = 0; j <= ny; j++) for (let i = 0; i <= nx; i++) {
    const u = i / nx, v = j / ny; // v=0が下端, v=1が上端(ヤード)
    const width = w * (0.72 + 0.28 * v);                                 // 台形(下が72%)
    const belly = Math.sin(u * Math.PI) * (0.5 + 0.5 * Math.sin(v * Math.PI)) * w * 0.24; // 風のふくらみ
    const flutter = v < 0.15 ? Math.sin(u * Math.PI * 3) * 1.4 * (1 - v / 0.15) : 0;      // 下端の波打ち
    pos.push((u - 0.5) * width, y0 + v * h + flutter * 0.3, belly + flutter);
  }
  const row = nx + 1;
  for (let j = 0; j < ny; j++) for (let i = 0; i < nx; i++) { const a = j * row + i; idx.push(a, a + row, a + row + 1, a, a + row + 1, a + 1); }
  return smoothNormals(makeGeom(pos, idx));
}

// シーンからテクスチャ付き材質をcloneして流用（船のハル=木材など）。元材質は変更しない。
export function harvestTexturedMat(scene: any, re: RegExp): any {
  let found: any = null;
  scene.traverse((o: any) => { if (found || !o.isMesh || !o.material) return; const ms = Array.isArray(o.material) ? o.material : [o.material]; for (const m of ms) if (m && re.test(m.name || '')) { found = m; break; } });
  return found && found.clone ? found.clone() : null;
}
// (旧 logGeomUV は cylinderGeomUV に統合済み: taper/擬似AO/uScale対応の上位互換)
// 丸太メッシュ。makeGeom経由=非indexedで属性count一致→Safari可。
// テクスチャ有: 円筒UV+Palme幹テクスチャ / 無: 頂点カラーの木目にフォールバック。
function logMesh(r: number, len: number, m: any, along: 'x' | 'z', seed: number, textured: boolean, taper = 1): any {
  const geo = textured
    ? cylinderGeomUV(r, len, 10, 34, (seed * 0.37) % 1, (seed * 0.61) % 1, taper, along === 'z' ? 'sin' : 'cos') // UV個体差+先細り+疑似AO
    : woodColors(cylinderGeom(r * taper, r, len, 10), seed);
  const mesh = new T.Mesh(geo, m);
  if (along === 'z') { mesh.rotation.x = Math.PI / 2; mesh.position.z = -len / 2; } // +Z方向→中心化
  else { mesh.rotation.z = -Math.PI / 2; mesh.position.x = -len / 2; }              // +X方向→中心化
  return mesh;
}

function buildRaft(scene: any): any {
  // 木材: 椰子の幹(Palme)の実テクスチャを流用(makeGeomのcount一致でSafariも安全)。
  // 見つからなければ頂点カラーの木目にフォールバック。帆は難破船の帆布材質を流用。
  const hull = harvestTexturedMat(scene, /dutch_ship_medium_hull/i) || harvestTexturedMat(scene, /^Palme$/i);
  const textured = !!hull;
  if (textured) { hull.vertexColors = true; hull.needsUpdate = true; } // 疑似AO(頂点カラー)を乗算
  const wood = hull || matVC(0x7a4a24);
  const wood2 = textured ? hull.clone() : matVC(0x5f3a1e);
  const rope = mat(0x3a2c18);
  const shipSail = harvestTexturedMat(scene, /dutch_ship_medium_sails/i);
  if (shipSail) { shipSail.vertexColors = true; shipSail.needsUpdate = true; } // clothColorsの布シェーディング(継ぎ目/折り目/裾汚れ)を有効化(無効だと無地グレー)
  const sail = shipSail || matVC(0xe8dcc0, 0.95);
  const g = new T.Group();
  const W = 54, D = 60, R = 2.7, deckTop = R * 2;
  const n = 9, gap = W / (n - 1);
  let s0 = 4242; const rnd = () => { s0 = (1103515245 * s0 + 12345) & 0x7fffffff; return s0 / 0x7fffffff; };
  // 甲板: 不揃いな丸太(半径±15%・長さ±10%・先細り・微妙なズレ/傾き)=拾い集めた流木感
  const logX: number[] = [];
  for (let i = 0; i < n; i++) {
    const rr = R * (0.85 + 0.3 * rnd());
    const len = D * (0.92 + 0.13 * rnd());
    const log = logMesh(rr, len, wood, 'z', i + 3, textured, 0.8 + 0.15 * rnd());
    const x = -W / 2 + i * gap + (rnd() - 0.5) * 1.4;
    logX.push(x);
    log.position.x = x;
    log.position.y = rr;                       // 下端(海側)を揃える
    log.position.z = (rnd() - 0.5) * 5;        // 前後に不揃い
    log.rotation.y = (rnd() - 0.5) * 0.05;     // わずかに傾く
    g.add(log);
  }
  // 上横木(細め)2本: 丸太の上に直交して載せ、これを縛って束ねている構造に見せる
  const beamZ = [-D * 0.32, D * 0.32];
  for (let k = 0; k < 2; k++) {
    const beam = logMesh(R * 0.55, W + R * 2.4, wood2, 'x', 30 + k, textured, 0.92);
    beam.position.y = deckTop + R * 0.5;
    beam.position.z = beamZ[k];
    g.add(beam);
  }
  // 縛り紐: 各丸太×横木の交差部に「八の字」の斜め紐(2本)。棒状の帯より遥かに本物らしい
  const ropeM = rope;
  for (let i = 0; i < n; i++) {
    for (let k = 0; k < 2; k++) {
      for (const sgn of [-1, 1]) {
        const tieR = new T.Mesh(cylinderGeom(0.42, 0.42, R * 3.6, 5), ropeM);
        tieR.position.set(logX[i], R * 0.7, beamZ[k] + sgn * 0.8);
        tieR.rotation.x = sgn * 0.45;          // 前後に開く八の字
        tieR.rotation.z = (rnd() - 0.5) * 0.2; // 縛りの緩み
        g.add(tieR);
      }
    }
  }
  // 甲板の小物: 樽(ロフト+タガ)とロープコイル=生活感
  {
    const barrelM = matVC(0x7a5a34, 0.8); // loftGeomはUV無しのためテクスチャでなく頂点カラーの木目で
    const barrel = new T.Mesh(loftGeom([
      { z: -5, rx: 3.3, ry: 3.3 }, { z: -3, rx: 4.3, ry: 4.3 }, { z: 0, rx: 4.7, ry: 4.7 },
      { z: 3, rx: 4.3, ry: 4.3 }, { z: 5, rx: 3.3, ry: 3.3 },
    ], 10), barrelM);
    woodColors(barrel.geometry, 123); // 樽の木目
    barrel.rotation.x = Math.PI / 2;
    barrel.position.set(W * 0.3, deckTop + 5.2, D * 0.3);
    g.add(barrel);
    const hoopM = mat(0x2a2a2e);
    for (const hz of [-3.4, 3.4]) {
      const hoop = new T.Mesh(cylinderGeom(4.45, 4.45, 0.8, 12), hoopM);
      hoop.position.set(W * 0.3, deckTop + 5.2 + hz, D * 0.3);
      g.add(hoop);
    }
    const coil = new T.Mesh(cylinderGeom(2.6, 2.9, 1.6, 10), ropeM); // 巻いたロープの束
    coil.position.set(-W * 0.32, deckTop + R * 0.6, -D * 0.28);
    g.add(coil);
  }
  // マスト + ヤード(横桁) + たわんだ帆
  // マスト: 椰子の幹(Palme)テクスチャの一本柱。いかだ=椰子材の世界観に合い、船体アトラス流用で
  // 起きる小物の写り込み(青ランタン等がv折り返しで混入)も避けられる。アトラスは黒背景に幹ストリップが
  // 並ぶ構成のため、ストリップ内側(u0.22-0.27 / v0.12-0.80)だけをサンプルする。
  const palmM = harvestTexturedMat(scene, /^Palme$/i);
  const mastGeo = palmM ? cylinderGeomUV(R * 0.58, D * 0.72, 10, 64, 0.22, 0.12, 1, 'none', 0.05)
    : textured ? cylinderGeomUV(R * 0.58, D * 0.72, 10, 26, 0.2, 0.55, 1, 'none', 0.35)
    : woodColors(cylinderGeom(R * 0.55, R * 0.62, D * 0.72, 10), 99);
  const mast = new T.Mesh(mastGeo, palmM || wood2); mast.position.set(0, deckTop, D * 0.2); g.add(mast);
  // マスト根本の支え木(斜め2本) — ただの垂直棒に見えないように
  for (const sgn of [-1, 1]) {
    const brace = logMesh(R * 0.4, D * 0.3, wood2, 'z', 88 + sgn, textured, 0.9);
    brace.position.set(sgn * 1.2, deckTop, D * 0.2);
    brace.rotation.x = -0.9; brace.rotation.z = sgn * 0.5;
    g.add(brace);
  }
  const yard = logMesh(R * 0.3, W * 0.66, wood2, 'x', 77, textured); yard.position.set(0, deckTop + D * 0.52, D * 0.2); g.add(yard);
  const sailMesh = new T.Mesh(clothColors(sailGeom(W * 0.62, D * 0.44, deckTop + D * 0.14)), sail); sailMesh.position.z = D * 0.2; sailMesh.name = 'raft-sail'; g.add(sailMesh); // main側tickが風で揺らす
  // リギング(張り綱): ヤード両端→甲板の前後へ細いロープを張る(帆船らしさ)
  {
    const ropeM = mat(0x2e2416);
    const tie = (x0: number, y0: number, z0: number, x1: number, y1: number, z1: number) => {
      const dx = x1 - x0, dy = y1 - y0, dz = z1 - z0;
      const len = Math.hypot(dx, dy, dz);
      const m = new T.Mesh(cylinderGeom(0.34, 0.34, len, 5), ropeM);
      // cylinderは+Y向き。中点に置き、(dx,dy,dz)方向へ回す
      m.position.set(x0, y0, z0);
      const yaw = Math.atan2(dx, dz);
      const pitch = Math.acos(Math.max(-1, Math.min(1, dy / len)));
      m.rotation.y = yaw; m.rotation.x = pitch; m.rotation.order = 'YXZ';
      g.add(m);
    };
    const yy = deckTop + D * 0.52, yz = D * 0.2, ex = W * 0.33;
    tie(ex, yy, yz, W * 0.42, R * 2, -D * 0.42);   // 右ヤード端→右舷前方
    tie(-ex, yy, yz, -W * 0.42, R * 2, -D * 0.42); // 左ヤード端→左舷前方
    tie(0, deckTop + D * 0.72, yz, 0, R * 2, D * 0.46); // マスト頂→船尾(バックステー)
  }
  return g;
}

// ---- 設置物: 焚き火 / 給水器 (全て makeGeom経由=非indexed=Safari安全) --------------
// 焚き火: 石の輪 + 組んだ薪 + 炎(オレンジのコーン)。実寸≈1m(world 20)。
export function spawnCampfire(scene: any, x: number, y: number, z: number): any {
  if (!harvestTHREE(scene) || !T.StdMat || !T.Group) return null;
  const g = new T.Group();
  const stone = mat(0x8a8a86);
  const hullM = harvestTexturedMat(scene, /dutch_ship_medium_hull/i);
  const woodm = hullM || matVC(0x5a3d20);
  for (let i = 0; i < 7; i++) { // 石の輪
    const a = (i / 7) * Math.PI * 2;
    const s = new T.Mesh(cylinderGeom(2.2, 2.8, 2.6, 6), stone);
    s.position.set(Math.cos(a) * 10, 0, Math.sin(a) * 10);
    g.add(s);
  }
  for (let i = 0; i < 4; i++) { // 薪(中心へ斜めに組む・船材テクスチャ)
    const a = (i / 4) * Math.PI * 2 + 0.4;
    const geo = hullM ? cylinderGeomUV(1.1, 13, 7, 26, i * 0.31, i * 0.17, 0.85) : woodColors(cylinderGeom(1.1, 1.1, 13, 7), i + 11);
    const w = new T.Mesh(geo, woodm);
    w.position.set(Math.cos(a) * 5.5, 0.5, Math.sin(a) * 5.5);
    w.rotation.z = 0.9 * Math.cos(a); w.rotation.x = -0.9 * Math.sin(a);
    g.add(w);
  }
  // 炎: 交差する2枚の炎形板ポリ(自発光)。main側のtickが name='campfire-flame' を揺らめかせる。
  const flameMat = new T.StdMat({ color: 0x201000, emissive: 0xff8a20, emissiveIntensity: 1.7, roughness: 1, metalness: 0, side: 2 });
  const flameProfile: [number, number][] = [[0, 1.5], [3.4, 4.5], [1.6, 9], [0, 13.5], [-1.7, 8.5], [-3.4, 4]];
  for (let k = 0; k < 2; k++) {
    const f = new T.Mesh(finGeom(flameProfile, 0.4), flameMat);
    f.rotation.y = k * Math.PI / 2;
    f.name = 'campfire-flame';
    g.add(f);
  }
  // 火の粉: 小さな自発光板8枚。main側のtickが name='campfire-ember' を上昇・明滅させる。
  const emberMat = new T.StdMat({ color: 0x100400, emissive: 0xffa540, emissiveIntensity: 2.2, roughness: 1, metalness: 0, side: 2, transparent: true, opacity: 0.9 });
  for (let k = 0; k < 8; k++) {
    const e = new T.Mesh(finGeom([[0, 0], [0.35, 0.2], [0, 0.45], [-0.35, 0.22]], 0.1), emberMat.clone()); // opacityを個別制御するためclone
    e.name = 'campfire-ember';
    e.userData.seed = k;
    g.add(e);
  }
  g.position.set(x, y, z); g.name = 'game-campfire';
  g.traverse((o: any) => { if (o.isMesh) o.frustumCulled = false; });
  scene.add(g);
  return g;
}

// 給水器: 三脚 + 布コーン(集水) + 受け皿。実寸≈1.2m。
export function spawnWaterStill(scene: any, x: number, y: number, z: number): any {
  if (!harvestTHREE(scene) || !T.StdMat || !T.Group) return null;
  const g = new T.Group();
  const hullM2 = harvestTexturedMat(scene, /dutch_ship_medium_hull/i);
  const woodm = hullM2 || matVC(0x6b4423), cloth = mat(0xdad2bd), basin = mat(0x4a5a66);
  for (let i = 0; i < 3; i++) { // 三脚(船材テクスチャ)
    const a = (i / 3) * Math.PI * 2;
    const leg = new T.Mesh(hullM2 ? cylinderGeomUV(0.9, 24, 6, 26, i * 0.29, i * 0.13, 0.9) : woodColors(cylinderGeom(0.9, 0.9, 24, 6), i + 31), woodm);
    leg.position.set(Math.cos(a) * 7, 0, Math.sin(a) * 7);
    leg.rotation.z = -0.28 * Math.cos(a); leg.rotation.x = 0.28 * Math.sin(a);
    g.add(leg);
  }
  const funnel = new T.Mesh(coneGeom(9, 8, 9, 14), cloth); g.add(funnel);          // 布の集水コーン(逆さ)
  const pot = new T.Mesh(cylinderGeom(4.5, 3.6, 3.2, 9), basin); g.add(pot);       // 受け皿
  const water = new T.Mesh(cylinderGeom(4.1, 4.1, 0.4, 9, 2.9), mat(0x3fa8c8));    // 貯まった水面(貯水で表示)
  water.name = 'still-water'; water.visible = false; g.add(water);
  g.position.set(x, y, z); g.name = 'game-waterstill';
  g.traverse((o: any) => { if (o.isMesh) o.frustumCulled = false; });
  scene.add(g);
  return g;
}

// シェルター(我が家): A型フレームの漂流者小屋。屋根板2枚+棟木+背面三角壁+床台+寝床。
// 実寸≈2.4m幅。屋根/床は船材テクスチャ(boxGeomはUV持ち)、三角壁はUV無しfinGeomのため頂点カラー。
export function spawnShelter(scene: any, x: number, y: number, z: number, yaw = 0): any {
  if (!harvestTHREE(scene) || !T.StdMat || !T.Group) return null;
  const g = new T.Group();
  const hullM = harvestTexturedMat(scene, /dutch_ship_medium_hull/i);
  const woodm = hullM || matVC(0x6b4a28);
  const vcWood = matVC(0x8a6a42, 0.95); // 三角壁用(頂点カラー)
  const W = 48, D = 44, H = 42;          // 底辺幅/奥行/棟高
  const slope = Math.atan2(W / 2, H);    // 屋根の傾き(垂直から)
  const slantLen = Math.hypot(W / 2, H) + 4; // 斜辺+軒の出
  for (const side of [-1, 1]) { // 屋根板(左右)
    const geo = hullM ? boxGeom(2.0, slantLen, D + 8) : woodColors(boxGeom(2.0, slantLen, D + 8), side + 5);
    const roof = new T.Mesh(geo, woodm);
    roof.position.set(side * W / 4, H / 2 + 1, 0);
    roof.rotation.z = side * slope;
    g.add(roof);
  }
  { // 棟木(頂上の丸太、奥行方向)
    const ridge = new T.Mesh(
      hullM ? cylinderGeomUV(1.5, D + 12, 7, 30, 0.23, 0.41, 1, 'sin') : woodColors(cylinderGeom(1.5, 1.5, D + 12, 7), 91),
      woodm,
    );
    ridge.rotation.x = Math.PI / 2;
    ridge.position.set(0, H + 1.5, (D + 12) / 2);
    g.add(ridge);
  }
  { // 背面の三角壁(finGeom=zy平面の輪郭をx厚み押し出し→yawで幅方向に向ける)
    const wall = new T.Mesh(woodColors(finGeom([[-W / 2 + 2, 0], [W / 2 - 2, 0], [0, H - 1]], 1.6), 47), vcWood);
    wall.rotation.y = Math.PI / 2; // 輪郭のz→世界x方向へ
    wall.position.set(0, 0, -D / 2 + 1);
    g.add(wall);
  }
  { // 床台と寝床マット
    const floor = new T.Mesh(hullM ? boxGeom(W - 8, 2.6, D - 4) : woodColors(boxGeom(W - 8, 2.6, D - 4), 17), woodm);
    floor.position.y = 1.3;
    g.add(floor);
    const mat2 = new T.Mesh(boxGeom(18, 1.4, 32), mat(0xc9bd8f)); // 繊維の敷物
    mat2.position.set(6, 3.2, 0);
    g.add(mat2);
  }
  g.position.set(x, y, z);
  g.rotation.y = yaw; // 開口(±z)を設置時の向きへ
  g.name = 'game-shelter';
  g.traverse((o: any) => { if (o.isMesh) o.frustumCulled = false; });
  scene.add(g);
  return g;
}

// ---- サメ(危険要素): ロフト胴体+ひれ板の本格メッシュ。makeGeom経由=Safari安全 -------------
// ロフト(輪切りの環を繋ぐ)ジオメトリ。断面は楕円(rx,ry)、yで中心線を上下に。
function loftGeom(rings: { z: number; rx: number; ry: number; y?: number }[], seg: number): any {
  const pos: number[] = [], idx: number[] = [];
  for (const r of rings) {
    for (let i = 0; i < seg; i++) {
      const a = (i / seg) * Math.PI * 2;
      pos.push(Math.cos(a) * r.rx, Math.sin(a) * r.ry + (r.y || 0), r.z);
    }
  }
  for (let k = 0; k < rings.length - 1; k++) {
    for (let i = 0; i < seg; i++) {
      const a = k * seg + i, b = k * seg + ((i + 1) % seg), c = (k + 1) * seg + ((i + 1) % seg), d = (k + 1) * seg + i;
      idx.push(a, b, c, a, c, d);
    }
  }
  // 両端キャップ(中心点)
  const n = pos.length / 3;
  const first = rings[0], last = rings[rings.length - 1];
  pos.push(0, first.y || 0, first.z); pos.push(0, last.y || 0, last.z);
  for (let i = 0; i < seg; i++) {
    idx.push(n, (i + 1) % seg, i);
    idx.push(n + 1, (rings.length - 1) * seg + i, (rings.length - 1) * seg + ((i + 1) % seg));
  }
  return makeGeom(pos, idx);
}
// 直方体(箱)ジオメトリ。各面を独立4頂点で張る(makeGeomが非indexed展開→面ごとにフラット法線)。
// w=幅(x) h=高さ(y) d=奥行(z)。原点は箱の中心。木箱・宝箱などの土台。
function boxGeom(w: number, h: number, d: number): any {
  const x = w / 2, y = h / 2, z = d / 2;
  const pos: number[] = [], idx: number[] = [], uv: number[] = [];
  const UVS = 20; // world→UVのスケール(船材テクスチャ用)
  const quad = (a: number[], b: number[], c: number[], e: number[]) => {
    const n = pos.length / 3;
    pos.push(...a, ...b, ...c, ...e);
    uv.push(0, 0, w / UVS, 0, w / UVS, h / UVS, 0, h / UVS); // 面ごとに平面貼り(テクスチャが黒くならないためのUV)
    idx.push(n, n + 1, n + 2, n, n + 2, n + 3);
  };
  quad([-x, -y, z], [x, -y, z], [x, y, z], [-x, y, z]);       // +Z
  quad([x, -y, -z], [-x, -y, -z], [-x, y, -z], [x, y, -z]);   // -Z
  quad([x, -y, z], [x, -y, -z], [x, y, -z], [x, y, z]);       // +X
  quad([-x, -y, -z], [-x, -y, z], [-x, y, z], [-x, y, -z]);   // -X
  quad([-x, y, z], [x, y, z], [x, y, -z], [-x, y, -z]);       // +Y(天面)
  quad([-x, -y, -z], [x, -y, -z], [x, -y, z], [-x, -y, z]);   // -Y(底面)
  return makeGeom(pos, idx, uv);
}

// ---- 沈没クレート(海中サルベージ): 海底に眠る物資箱。潜って開封→物資 --------------
// 板の木箱 + ロープの十字掛け。船材テクスチャがあれば流用、無ければ頂点カラーの木目。
// 半分傾けて海底に打ち捨てられた感を出す。makeGeom経由=Safari安全。
export function spawnCrate(scene: any, x: number, y: number, z: number, opts: { seed?: number } = {}): any {
  if (!harvestTHREE(scene) || !T.StdMat || !T.Group) return null;
  const seed = opts.seed ?? 1;
  const g = new T.Group();
  // 深海は光が減衰するので明るめの頂点カラー木目(テクスチャより視認性優先)
  const woodm = matVC(0xa87d4e, 0.9);
  const rope = mat(0x4a3a24);
  const S = 11; // 箱の一辺(world ≈0.55m)
  const box = new T.Mesh(woodColors(boxGeom(S, S * 0.85, S), seed * 7 + 3), woodm);
  g.add(box);
  // 板の桟(横木2本×前後)=箱らしさ
  for (const zz of [-S / 2 - 0.3, S / 2 + 0.3]) {
    for (const yy of [-S * 0.22, S * 0.22]) {
      const slat = new T.Mesh(woodColors(boxGeom(S + 1.2, S * 0.16, 0.8), seed + yy), woodm);
      slat.position.set(0, yy, zz);
      g.add(slat);
    }
  }
  // ロープの十字掛け(2本)
  for (const rot of [0, Math.PI / 2]) {
    const band = new T.Mesh(boxGeom(1.6, S * 0.92, S + 1.4), rope);
    band.rotation.y = rot;
    g.add(band);
  }
  // 打ち捨てられた傾き(個体差)
  let s0 = (seed * 2654435761) >>> 0 || 1; const rnd = () => { s0 = (1103515245 * s0 + 12345) & 0x7fffffff; return s0 / 0x7fffffff; };
  g.rotation.set((rnd() - 0.5) * 0.5, rnd() * Math.PI * 2, (rnd() - 0.5) * 0.5);
  g.position.set(x, y, z);
  g.name = 'game-crate';
  g.traverse((o: any) => { if (o.isMesh) o.frustumCulled = false; });
  scene.add(g);
  return g;
}

// 薄いひれ板。輪郭=(z,y)の点列をX方向に厚みthで押し出し(輪郭は凸前提の扇張り)。
function finGeom(profile: [number, number][], th: number): any {
  const pos: number[] = [], idx: number[] = [], n = profile.length;
  for (const [z, y] of profile) { pos.push(-th / 2, y, z); pos.push(th / 2, y, z); }
  for (let i = 0; i < n - 1; i++) {
    const a = i * 2, b = i * 2 + 1, c = (i + 1) * 2 + 1, d = (i + 1) * 2;
    idx.push(a, b, c, a, c, d);
  }
  for (let i = 1; i < n - 1; i++) { idx.push(0, i * 2, (i + 1) * 2); idx.push(1, (i + 1) * 2 + 1, i * 2 + 1); }
  return makeGeom(pos, idx);
}
// サメの体色(上=灰青 下=白 のカウンターシェーディング)を頂点カラーで。
function sharkColors(geo: any): any {
  const pa = geo.attributes.position, n = pa.count, col = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    const y = pa.getY(i);
    const t = Math.max(0, Math.min(1, 0.5 - y / 14)); // 上→下で 0→1
    col[i * 3] = 0.30 + 0.55 * t; col[i * 3 + 1] = 0.34 + 0.52 * t; col[i * 3 + 2] = 0.40 + 0.46 * t;
  }
  geo.setAttribute('color', new T.BufferAttribute(col, 3));
  return geo;
}

export function spawnShark(scene: any): any {
  if (!harvestTHREE(scene) || !T.StdMat || !T.Group) return null;
  const g = new T.Group();
  const skin = matVC(0xffffff, 0.62); // 頂点カラー + やや艶(濡れた肌)
  // 胴体: 鼻先→頭→胴→尾柄の滑らかなロフト(全長≈3.4m=68world、縦長楕円断面)
  const B = (z: number, r: number, sq = 1.12, y = 0) => ({ z, rx: r, ry: r * sq, y });
  const body = loftGeom([
    B(-34, 0.8, 1, -1), B(-30, 2.6, 1.05, -0.6), B(-24, 4.6, 1.1, 0), B(-14, 6.4, 1.14, 0.3),
    B(-2, 7.2, 1.16, 0.3), B(10, 6.6, 1.14, 0.2), B(20, 4.6, 1.1, 0), B(28, 2.4, 1.05, 0), B(34, 1.2, 1, 0.4),
  ], 14);
  g.add(new T.Mesh(sharkColors(body), skin));
  // 背びれ(後退した三日月型)
  g.add(new T.Mesh(sharkColors(finGeom([[-6, 7], [2, 16.5], [6, 15], [1, 8.5], [6, 7.5]], 1.1)), skin));
  // 尾びれ(上葉の長いヘテロセルカル)
  g.add(new T.Mesh(sharkColors(finGeom([[32, 1], [46, 13], [42, 4], [40, -6], [35, -1.5]], 1)), skin));
  // 胸びれ 左右(斜め下へ開く)
  for (const s of [-1, 1]) {
    const pec = new T.Mesh(sharkColors(finGeom([[-14, 0], [-2, -3], [2, -2.4], [-8, 1.5]], 0.9)), skin);
    pec.position.set(s * 6, -4, 0); pec.rotation.z = s * 0.7;
    g.add(pec);
  }
  // 背びれの引き波(V字の白い薄板×2) — 水面を切って近づく恐怖の記号
  const wakeM = mat(0xe8f4f6);
  for (const s of [-1, 1]) {
    const wake = new T.Mesh(finGeom([[4, 5.0], [34, 4.6], [34, 5.6], [4, 6.4]], 0.5), wakeM);
    wake.position.set(s * 1.6, 0, 2);
    wake.rotation.y = s * 0.24; // V字に開く
    g.add(wake);
  }
  g.name = 'game-shark';
  g.traverse((o: any) => { if (o.isMesh) o.frustumCulled = false; });
  g.visible = false; // 出現条件を満たすまで非表示
  scene.add(g);
  return g;
}

// ---- 一人称の手持ちアイテム(銛/斧): AAAの「手触り」の核 --------------------------
// カメラはロック方式(main.tsが毎フレーム姿勢を直書き)のため、カメラの子ではなく
// 「毎フレームworld空間で目線の右下へ配置」する。mainのtickが位置/回転/振りを更新する。
// 局所座標系: -Z=前方(突く方向)。銛=長い柄+石の穂先+縛り紐 / 斧=柄+石の刃。
export function spawnHeldItem(scene: any): any {
  if (!harvestTHREE(scene) || !T.StdMat || !T.Group) return null;
  const root = new T.Group();
  root.name = 'game-held';
  const hullM = harvestTexturedMat(scene, /dutch_ship_medium_hull/i);
  if (hullM) { hullM.vertexColors = true; hullM.needsUpdate = true; }
  const woodm = hullM || matVC(0x7a5230, 0.85);
  const stone = mat(0x777a78);
  const lash = mat(0x3a2c18);

  // --- 銛(spear): 柄46 + 石の穂先 + 穂先の縛り ---
  const spear = new T.Group(); spear.name = 'held-spear';
  {
    const shaft = new T.Mesh(
      hullM ? cylinderGeomUV(0.85, 46, 8, 30, 0.42, 0.23, 0.92) : woodColors(cylinderGeom(0.8, 0.9, 46, 8), 5),
      woodm);
    shaft.rotation.x = -Math.PI / 2; // +Y→-Z(前方)
    spear.add(shaft);
    const tip = new T.Mesh(coneGeom(1.5, 6.5, 6), stone);
    tip.rotation.x = -Math.PI / 2; tip.position.z = -46;
    spear.add(tip);
    const wrap = new T.Mesh(cylinderGeom(1.15, 1.15, 3.2, 6), lash);
    wrap.rotation.x = -Math.PI / 2; wrap.position.z = -44.4;
    spear.add(wrap);
  }
  root.add(spear);

  // --- 斧(axe): 柄26 + 石の刃(fin) + 縛り ---
  const axe = new T.Group(); axe.name = 'held-axe';
  {
    const handle = new T.Mesh(
      hullM ? cylinderGeomUV(0.95, 26, 7, 26, 0.11, 0.53, 0.9) : woodColors(cylinderGeom(0.9, 1.0, 26, 7), 9),
      woodm);
    handle.rotation.x = -Math.PI / 2; // -Z方向へ
    axe.add(handle);
    const blade = new T.Mesh(finGeom([[-3.2, 0], [-4.6, 3.6], [0.4, 5.2], [2.6, 2.2], [1.2, 0]], 1.6), stone);
    blade.position.z = -23; blade.rotation.x = 0.15;
    axe.add(blade);
    const wrap = new T.Mesh(cylinderGeom(1.3, 1.3, 3.0, 6), lash);
    wrap.rotation.x = -Math.PI / 2; wrap.position.z = -21.5;
    axe.add(wrap);
  }
  root.add(axe);

  // --- 松明(torch): 短い柄 + 布巻き + 先端の炎(自発光・mainが揺らめかせる) ---
  const torch = new T.Group(); torch.name = 'held-torch';
  {
    const handle = new T.Mesh(
      hullM ? cylinderGeomUV(0.95, 20, 7, 26, 0.63, 0.37, 0.92) : woodColors(cylinderGeom(0.9, 1.0, 20, 7), 21),
      woodm);
    handle.rotation.x = -Math.PI / 2;
    torch.add(handle);
    const wrap = new T.Mesh(cylinderGeom(1.5, 1.5, 4.2, 6), lash);
    wrap.rotation.x = -Math.PI / 2; wrap.position.z = -17.6;
    torch.add(wrap);
    const flameMat = new T.StdMat({ color: 0x201000, emissive: 0xff8a20, emissiveIntensity: 1.8, roughness: 1, metalness: 0, side: 2 });
    for (let k = 0; k < 2; k++) {
      const f = new T.Mesh(finGeom([[0, 0.5], [1.6, 1.8], [0.8, 4], [0, 6.2], [-0.9, 3.8], [-1.6, 1.6]], 0.3), flameMat);
      f.rotation.y = k * Math.PI / 2;
      f.position.z = -19.5;
      f.name = 'held-torch-flame';
      torch.add(f);
    }
  }
  root.add(torch);

  // --- 釣り竿(rod): 長い先細りの柄 + 手元の縛り。穂先を上げた構えは局所回転で作る ---
  const rod = new T.Group(); rod.name = 'held-rod';
  {
    const shaft = new T.Mesh(
      hullM ? cylinderGeomUV(0.62, 64, 7, 40, 0.18, 0.66, 0.55) : woodColors(cylinderGeom(0.4, 0.75, 64, 7), 13),
      woodm);
    shaft.rotation.x = -Math.PI / 2; // +Y→-Z(前方)
    rod.add(shaft);
    const grip = new T.Mesh(cylinderGeom(1.05, 1.05, 5.5, 6), lash);
    grip.rotation.x = -Math.PI / 2; grip.position.z = -3;
    rod.add(grip);
    const tipMark = new T.Group(); tipMark.name = 'rod-tip'; tipMark.position.z = -64; // 釣り糸の始点
    rod.add(tipMark);
    rod.rotation.x = 0.3; // 穂先を上げて構える
  }
  root.add(rod);

  root.traverse((o: any) => { if (o.isMesh) o.frustumCulled = false; });
  root.visible = false; // mainのtickが所持品に応じて出し分ける
  scene.add(root);
  return root;
}

// ---- 釣りウキ: 赤い胴+白い頭。main側が釣り中だけ表示し、拝借ブイの波高へ追従させる ----
export function spawnFishFloat(scene: any): any {
  if (!harvestTHREE(scene) || !T.StdMat || !T.Group) return null;
  const g = new T.Group();
  g.name = 'game-float';
  g.add(new T.Mesh(cylinderGeom(0.5, 1.5, 3.4, 6), mat(0xc93a26)));
  g.add(new T.Mesh(cylinderGeom(0.42, 0.5, 2.0, 6, 3.4), mat(0xf3eee1)));
  g.traverse((o: any) => { if (o.isMesh) o.frustumCulled = false; });
  g.visible = false;
  scene.add(g);
  return g;
}

// ---- 開封の泡バースト: 丸いリング×12が立ち上る。mainがt進行/ビルボード/フェードを駆動 ----
// (三角形の浮遊粒子はユーザー指摘で禁止。泡は「丸いビルボード」= 毎フレームカメラ向き)
export function spawnBubbleBurst(scene: any): any {
  if (!harvestTHREE(scene) || !T.StdMat || !T.Group) return null;
  const g = new T.Group();
  g.name = 'game-bubbles';
  // 不透明: 透明材質は水中フォグとの相互作用の切り分けが難航したため避ける。フェードはスケール縮小で
  const m = new T.StdMat({ color: 0xffffff, emissive: 0xe8f7ff, emissiveIntensity: 1.3, roughness: 1, metalness: 0, side: 2 });
  const ring = (r: number) => { // 円環=泡の輪郭。中が抜けて水が透ける
    const pos: number[] = [], idx: number[] = [];
    const n = 12, ri = r * 0.7;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      pos.push(Math.cos(a) * r, Math.sin(a) * r, 0, Math.cos(a) * ri, Math.sin(a) * ri, 0);
    }
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      idx.push(2 * i, 2 * j, 2 * i + 1, 2 * i + 1, 2 * j, 2 * j + 1);
    }
    return makeGeom(pos, idx);
  };
  for (let i = 0; i < 12; i++) {
    const r = 0.9 + (i % 4) * 0.5; // 実寸(数cm)だと点にしかならない=演出として誇張
    const b = new T.Mesh(ring(r), m);
    b.userData = { ox: Math.sin(i * 2.7) * 5.6, oz: Math.cos(i * 1.9) * 5.6, vy: 6 + (i % 5) * 1.8, ph: i * 1.13 };
    g.add(b);
  }
  g.traverse((o: any) => { if (o.isMesh) o.frustumCulled = false; });
  g.visible = false;
  scene.add(g);
  return g;
}

// ---- 釣り糸: 単位長の細い円柱(+Z向き)。main側が竿先に置き lookAt+scale.z でウキまで張る ----
export function spawnFishLine(scene: any): any {
  if (!harvestTHREE(scene) || !T.StdMat) return null;
  const geo = cylinderGeom(0.07, 0.07, 1, 4);
  geo.rotateX(Math.PI / 2); // +Y→+Z(lookAtで狙う軸)
  const m = new T.Mesh(geo, mat(0xcfd6da));
  m.name = 'game-fishline';
  m.frustumCulled = false;
  m.visible = false;
  scene.add(m);
  return m;
}

// (水中の浮遊粒子motesは削除済み: 三角形粒子はユーザー指摘で禁止。泡=丸ビルボードのみ)

// いかだを scene の (x,z)・海面(y=0)へ追加して返す。size=倍率, yaw=向き。
export function spawnRaft(scene: any, x: number, z: number, opts: { size?: number; y?: number; yaw?: number } = {}): any {
  if (!harvestTHREE(scene) || !T.StdMat || !T.Group) return null;
  const raft = buildRaft(scene);
  const s = opts.size ?? 1; raft.scale.set(s, s, s);
  raft.position.set(x, opts.y ?? 0, z);
  if (opts.yaw) raft.rotation.y = opts.yaw;
  raft.name = 'game-raft';
  // WebKit(Safari)対策: 手組みジオメトリは frustumCulled=true だと視界内でも誤カリングされて
  // 描画されないことがある。全メッシュでカリングを無効化する（gputestの箱で唯一出た条件）。
  raft.traverse((o: any) => { if (o.isMesh) o.frustumCulled = false; });
  scene.add(raft);
  return raft;
}
